import React from "react";

export interface WizardStep {
  id: number;
  label: string;
  description?: string;
}

interface Props {
  steps: WizardStep[];
  current: number;
  /** Highest step the user is allowed to jump to (defaults to current). */
  maxReachable?: number;
  onStepClick?: (id: number) => void;
}

/**
 * Clean horizontal stepper for the timeline-creation wizard.
 * Numbered circles + labels + connectors, styled for the dark MES theme.
 */
const PlanWizardStepper: React.FC<Props> = ({ steps, current, maxReachable, onStepClick }) => {
  const reachable = maxReachable ?? current;

  return (
    <div className="plan-wizard-stepper">
      {steps.map((step, idx) => {
        const state =
          step.id < current ? "done" : step.id === current ? "active" : "todo";
        const clickable = !!onStepClick && step.id <= reachable;
        return (
          <React.Fragment key={step.id}>
            {idx > 0 && (
              <div className={`pw-connector ${step.id <= current ? "filled" : ""}`} />
            )}
            <button
              type="button"
              className={`pw-step pw-${state}`}
              onClick={clickable ? () => onStepClick!(step.id) : undefined}
              disabled={!clickable}
              aria-current={state === "active" ? "step" : undefined}
            >
              <span className="pw-circle">{state === "done" ? "✓" : step.id}</span>
              <span className="pw-text">
                <span className="pw-label">{step.label}</span>
                {step.description && <span className="pw-desc">{step.description}</span>}
              </span>
            </button>
          </React.Fragment>
        );
      })}

      <style>{`
        .plan-wizard-stepper {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 16px 18px;
          background: #2c3e50;
          border: 1px solid #3b4f66;
          border-radius: 10px;
          margin-bottom: 20px;
        }
        .pw-step {
          display: flex;
          align-items: center;
          gap: 12px;
          background: transparent;
          border: none;
          padding: 6px 8px;
          border-radius: 8px;
          cursor: default;
          text-align: left;
        }
        .pw-step:not(:disabled) { cursor: pointer; }
        .pw-step:not(:disabled):hover { background: rgba(255,255,255,0.04); }
        .pw-circle {
          flex-shrink: 0;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 15px;
          border: 2px solid #4a5f7f;
          background: #34495e;
          color: #95a5a6;
          transition: all 0.2s ease;
        }
        .pw-text { display: flex; flex-direction: column; line-height: 1.2; }
        .pw-label { color: #95a5a6; font-weight: 600; font-size: 14px; transition: color 0.2s ease; }
        .pw-desc { color: #6d7f92; font-size: 12px; margin-top: 2px; }

        .pw-active .pw-circle {
          border-color: #27ae60;
          background: #27ae60;
          color: #fff;
          box-shadow: 0 0 0 4px rgba(39,174,96,0.18);
        }
        .pw-active .pw-label { color: #ecf0f1; }

        .pw-done .pw-circle {
          border-color: #27ae60;
          background: rgba(39,174,96,0.15);
          color: #27ae60;
        }
        .pw-done .pw-label { color: #cdd6df; }

        .pw-connector {
          flex: 1;
          height: 2px;
          min-width: 24px;
          background: #4a5f7f;
          border-radius: 2px;
          transition: background 0.2s ease;
        }
        .pw-connector.filled { background: #27ae60; }
      `}</style>
    </div>
  );
};

export default PlanWizardStepper;
